"use client";
import { useState, useEffect } from "react";
import { useTapCounter } from "../../hooks/useTapCounter";
import { STRINGS } from "../../utils/strings";
import AnimatedButton from "./components/animatedBtn";
import { Link, useParams } from "react-router-dom";
import DefaultProfileImage from "../../../assets/backgrounds.jpg";
import { useGetMessagePublicIdQuery } from "../../redux/messageView";
import type {
  GetMessagePublicId,
  ReplyMessageRequest,
} from "../../redux/messageView/interface";
import { useReplyMessagePublicMutation } from "../../redux/messageView";
import toast from "react-hot-toast";
import { getRandomAnswer } from "../../utils/randomAnswers";

const Home = () => {
  const [question, setQuestion] = useState(STRINGS.questions[0]);
  const [message, setMessage] = useState("");
  const { count, increment } = useTapCounter();
  const [profileImage, setProfileImage] = useState<string>(DefaultProfileImage);
  const [username, setUsername] = useState<string>("@username");
  const { id } = useParams<{ id: string }>();
  const [replyMessage, { isLoading: isReplying }] =
    useReplyMessagePublicMutation();

  const {
    data: userData,
    isSuccess,
    isError,
  } = useGetMessagePublicIdQuery(id ?? "") as {
    data?: GetMessagePublicId;
    error?: any;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
  };

  useEffect(() => {
    const profilePic = userData?.user?.profile_picture;
    const uname = userData?.user?.username;

    if (isSuccess) {
      if (profilePic) {
        setProfileImage(profilePic);
        localStorage.setItem("profileImage", profilePic);
      }

      if (uname) {
        setUsername(`@${uname}`);
      }
    } else if (isError || !profilePic) {
      const storedImage = localStorage.getItem("profileImage");
      if (storedImage && !storedImage.startsWith("blob:")) {
        setProfileImage(storedImage);
      }
    }
  }, [isSuccess, isError, userData]);

  const handleRandomize = () => {
    const newQuestion = getRandomAnswer(question);
    setQuestion(newQuestion);
    setMessage(newQuestion);
  };

  const handleSubmit = async () => {
    if (!message.trim() || !userData?.data) return;

    const payload: ReplyMessageRequest = {
      receiver_id: userData.data.sender_id,
      content: message.trim(),
      parent_message_id: id ?? null as any,
    };

    try {
      const response = await replyMessage(payload).unwrap();
      console.log("Reply sent:", response);
      setMessage("");
      increment();
      toast.success("Reply sent successfully!");
    } catch (err) {
      toast.error("Failed to send reply. Please try again.");
      console.error("Failed to send reply:", err);
    }
  };

  // Get background colors from API response or use defaults
  const bgStart = userData?.data?.bgStart || "#000000";
  const bgEnd = userData?.data?.bgEnd || "#003AD3";
  
  const backgroundStyle = {
    background: `linear-gradient(to bottom right, ${bgStart}, ${bgEnd})`
  };

  return (
    <main className="h-screen flex flex-col items-center overflow-hidden no-scrollbar text-white pt-12 p-7" style={backgroundStyle}>
      <div className="text-black rounded-3xl w-full mt-10 max-w-[645px] shadow-lg overflow-hidden">
        <div className="bg-white flex items-center gap-3 p-2">
          <div className="w-10 h-10 ml-2 rounded-full overflow-hidden">
            <img
              src={profileImage}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-medium text-lg">{username}</p>
            <p className="text-md font-bold">
              {userData?.data?.content ?? "who would you ship me with?"}
            </p>
          </div>
        </div>

        <div className="bg-[rgba(255,255,255,0.4)] p-4 relative">
          <textarea
            placeholder="send me anonymous messages..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="bg-transparent w-full outline-none text-xl placeholder-black/30 font-bold text-black "
            rows={3}
          />
          <div className="flex justify-end">
            <button
              onClick={handleRandomize}
              className="bg-[rgba(255,255,255,0.2)] rounded-full p-2 w-10 h-10 flex items-center justify-center"
            >
              <span className="text-xl">🎲</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-center my-4">
        <p className="text-sm text-white flex items-center gap-1">
          🔒 anonymous q&a
        </p>
      </div>

      {message.trim() && (
        <button
          onClick={handleSubmit}
          disabled={isReplying}
          className={`mt-4 w-full max-w-[645px] text-lg font-bold py-4 px-6 rounded-full shadow-lg transition-all duration-200 hover:scale-105 ${isReplying
              ? "bg-gray-600 cursor-not-allowed text-gray-300"
              : "bg-black hover:bg-gray-900 text-white"
            }`}
        >
          {isReplying ? "Sending…" : "Send!"}
        </button>
      )}

      <div className="w-full max-w-[645px] p-4 mt-8 mb-8 absolute bottom-0">
        <div
          className={`items-center justify-center text-center transition-opacity duration-300 ${message.trim() ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
        >
          <p className="text-white text-xl font-bold">
            👇 {count} people just tapped the button 👇
          </p>
          <Link to="https://play.google.com/store/apps/details?id=com.ng.brochat">
            <AnimatedButton>Get your own messages!</AnimatedButton>
          </Link>

          <div className="mt-5 text-sm text-gray-400 flex justify-center gap-3">
            <a href="#" className="hover:text-white font-bold">
              Terms
            </a>
            <a href="#" className="hover:text-white font-bold">
              Privacy
            </a>
          </div>
          <div className="h-10"></div>
        </div>
      </div>
    </main>
  );
};

export default Home;
